import {SINGLE_DATASET_INPUT_STEP_DATA_NAME} from 'analysis-tool/AnalysisStepDataNames';
import {DATA_FRAME_DISPLAY_ROW_LIMIT} from 'analysis-tool/AnalysisToolConstants';
import {isViewTerminalStepType, VIEW_END_STEP_TYPES} from 'analysis-tool/data/LineageStepTypes';
import {
  ButtonDataViewAction,
  DataViewActionInput,
  rowSelectionState,
} from 'analysis-tool/data-view/DataViewContext';
import isAnalysisReadOnly from 'analysis-tool/isAnalysisReadOnly';
import {recordAnalysisAction} from 'analysis-tool/ui/debugAnalysisActionHistory';
import {openFlagRowsModal} from 'analysis-tool/ui/flag-rows/FlagRowsModal';
import {getFlaggedRowsFromDataFrame} from 'analysis-tool/ui/flag-rows/FlagRowUtil';
import {assert} from 'Asserts';
import {PersistedDataFrameGraphQL} from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import hasDownstreamFlagRowsStep from 'pipelines/hasDownstreamFlagRowsStep';
import {StepReference} from 'pipelines/network/PipelineDAO';
import {PipelineGraphQL, PipelineViewGraphQL, PipelineViewLineageNode} from 'pipelines/PipelineTypes';
import {resolveLineageStep, resolveOutputStepData} from 'pipelines/PipelineViewUtils';
import {getChildStepsForDataFrame} from 'pipelines/StepLineageUtils';
import {getLegacyStepFromAnalysis} from 'pipelines/StepUtil';

export default function getFlaggingActions(
  analysis: PipelineGraphQL,
  view: PipelineViewGraphQL,
  clientId: string,
  selectedRowIndices: Set<number>
): Array<ButtonDataViewAction | null> {
  if (!isFlaggingEnabled(analysis, view, selectedRowIndices)) {
    return [];
  }

  return [
    getFlagRowsAction(analysis, view, clientId, selectedRowIndices),
    getClearFlagRowsAction(analysis, view, clientId, selectedRowIndices),
  ];
}

function isFlaggingEnabled(
  analysis: PipelineGraphQL,
  view: PipelineViewGraphQL,
  selectedRowIndices: Set<number>
): boolean {
  if (view.lineage.length === 0) {
    return false;
  }
  const lastNode = view.lineage[view.lineage.length - 1];
  const lastStep = resolveLineageStep(analysis.steps, lastNode);
  return (
    !isAnalysisReadOnly(analysis) &&
    view.lineage.every(
      (lineageNode) =>
        resolveLineageStep(analysis.steps, lineageNode).statusCode === PipelineStepStatusCode.SUCCEEDED
    ) &&
    (selectedRowIndices.size > 0 || lastStep.stepType === PipelineStepType.CHART_VIEW)
  );
}

function getFlagRowsAction(
  analysis: PipelineGraphQL,
  view: PipelineViewGraphQL,
  clientId: string,
  selectedRowIndices: Set<number>
): ButtonDataViewAction {
  return ({persistedDataFrame, allowVisualFlagging}: DataViewActionInput) => {
    if (persistedDataFrame == null) {
      return null;
    }

    if (allowVisualFlagging === false) {
      return {
        icon: 'flag',
        isAlternativeButtonColor: true,
        onClick: () => {},
        helpText: 'Rerun chart to enable point flagging.',
        label: 'Flag',
        isDisabled: true,
      };
    }

    if (selectedRowIndices.size === 0) {
      return null;
    }

    const flaggedRowIndices = getFlaggedRowIndices(analysis, view, persistedDataFrame);
    const disabledMessage = getFlagRowDisabledMessage(analysis, view, persistedDataFrame);

    return {
      icon: 'flag',
      isAlternativeButtonColor: true,
      onClick: () => {
        onToggleFlaggedRows(
          analysis,
          view,
          persistedDataFrame,
          clientId,
          selectedRowIndices,
          flaggedRowIndices,
          true
        );
      },
      helpText: disabledMessage ?? 'Flag rows',
      label: 'Flag',
      isDisabled: disabledMessage != null,
    };
  };
}

function getFlagRowDisabledMessage(
  analysis: PipelineGraphQL,
  view: PipelineViewGraphQL,
  persistedDataFrame: PersistedDataFrameGraphQL
): string | null {
  const lastStep = view.lineage[view.lineage.length - 1];
  const legacyLastStep = getLegacyStepFromAnalysis(analysis, lastStep.step);

  if (persistedDataFrame.numRows > DATA_FRAME_DISPLAY_ROW_LIMIT) {
    return 'Flagging rows is disabled for large tables.';
  }

  if (hasDownstreamFlagRowsStep(analysis, legacyLastStep)) {
    return 'This view cannot be flagged because one or more downstream views contain flagged rows.';
  }

  return null;
}

function getClearFlagRowsAction(
  analysis: PipelineGraphQL,
  view: PipelineViewGraphQL,
  clientId: string,
  selectedRowIndices: Set<number>
): ButtonDataViewAction {
  return ({persistedDataFrame, allowVisualFlagging}: DataViewActionInput) => {
    if (persistedDataFrame == null) {
      return null;
    }

    if (allowVisualFlagging === false) {
      return {
        icon: 'flag-slash',
        isAlternativeButtonColor: true,
        onClick: () => {},
        helpText: 'Rerun chart to enable point flagging.',
        label: 'Clear',
        isDisabled: true,
      };
    }

    if (selectedRowIndices.size === 0) {
      return null;
    }

    const flaggedRowIndices = getFlaggedRowIndices(analysis, view, persistedDataFrame);
    const disabledMessage = getClearFlagRowDisabledMessage(
      analysis,
      view,
      persistedDataFrame,
      selectedRowIndices,
      flaggedRowIndices
    );

    return {
      icon: 'flag-slash',
      isAlternativeButtonColor: true,
      onClick: () => {
        onToggleFlaggedRows(
          analysis,
          view,
          persistedDataFrame,
          clientId,
          selectedRowIndices,
          flaggedRowIndices,
          false
        );
      },
      helpText: disabledMessage ?? 'Clear flag rows',
      label: 'Clear',
      isDisabled: disabledMessage != null,
    };
  };
}

function getClearFlagRowDisabledMessage(
  analysis: PipelineGraphQL,
  view: PipelineViewGraphQL,
  persistedDataFrame: PersistedDataFrameGraphQL,
  selectedRowIndices: Set<number>,
  flaggedRowIndices: Set<string>
): string | null {
  const lastStep = view.lineage[view.lineage.length - 1];
  const legacyLastStep = getLegacyStepFromAnalysis(analysis, lastStep.step);

  if (persistedDataFrame.numRows > DATA_FRAME_DISPLAY_ROW_LIMIT) {
    return 'Clearing flags is disabled for large tables.';
  }

  if ([...selectedRowIndices].every((rowIndex) => !flaggedRowIndices.has(String(rowIndex)))) {
    return 'No flagged rows are selected.';
  }

  if (hasDownstreamFlagRowsStep(analysis, legacyLastStep)) {
    return 'This view cannot be flagged because one or more downstream views contain flagged rows.';
  }

  return null;
}

/**
 * Get the flagged row indices for a view.
 *
 * For terminal step types (REGRESSION, CHART_VIEW, TABLE_VIEW), flagged rows are stored on the
 * parent dataset step, not the terminal step itself. This function uses the analysis graph to
 * find the correct step to check for flagged rows.
 *
 * @package exported for testing
 */
export function getFlaggedRowIndices(
  analysis: PipelineGraphQL,
  view: PipelineViewGraphQL,
  persistedDataFrame: PersistedDataFrameGraphQL
): Set<string> {
  const lastNode = view.lineage[view.lineage.length - 1];
  const lastNodeStep = resolveLineageStep(analysis.steps, lastNode);

  const nodeToCheck = isViewTerminalStepType(lastNodeStep.stepType)
    ? (getParentNodeFromAnalysisGraph(analysis, lastNode) ?? lastNode)
    : lastNode;

  const legacyLastStep = getLegacyStepFromAnalysis(analysis, nodeToCheck.step);

  return getFlaggedRowsFromDataFrame(analysis, legacyLastStep, persistedDataFrame.dataFrameJson.rows);
}

/**
 * Get the parent node from the analysis graph by walking up the step's incoming edges.
 *
 * This is used for terminal step types (REGRESSION, CHART_VIEW, TABLE_VIEW) to find the parent
 * dataset step that should receive the FLAG_ROWS child step. This approach is more robust than
 * relying on view lineage, which may have different structures depending on how views are calculated.
 *
 * We search by step data name "Input dataset" rather than kind because terminal steps may have
 * multiple dataset inputs, and the name identifies the primary input (framework convention matching backend).
 *
 * @returns The parent node, or null if:
 *  - The step has no incoming edge to "Input dataset" (valid for root steps)
 *  - The parent step or output data cannot be found (indicates inconsistent analysis state,
 *    which can occur during concurrent updates while the GraphQL response is being generated.
 *    See isPipelineGraphQLInternallyConsistent for more details on this known issue.)
 */
function getParentNodeFromAnalysisGraph(
  analysis: PipelineGraphQL,
  childNode: PipelineViewLineageNode
): PipelineViewLineageNode | null {
  const childStep = resolveLineageStep(analysis.steps, childNode);
  // Defensive check: this function only supports the known terminal step types
  // that have a single 'Input dataset' edge. If a new terminal step type is added to
  // VIEW_END_STEP_TYPES, this function must be updated to handle it.
  assert(
    VIEW_END_STEP_TYPES.includes(childStep.stepType),
    'getParentNodeFromAnalysisGraph only supports terminal step types with single dataset input',
    {
      stepType: childStep.stepType,
      supportedTypes: VIEW_END_STEP_TYPES,
    }
  );

  const datasetInputEdge = childStep.incomingEdges.find(
    (edge) => edge.toStepData.name === SINGLE_DATASET_INPUT_STEP_DATA_NAME
  );

  if (!datasetInputEdge) {
    // For the supported terminal step types, not having an 'Input dataset' edge is unexpected
    // and indicates either data corruption or a step type that doesn't match our assumptions
    assert(false, 'Expected terminal step to have an incoming edge to "Input dataset"', {
      stepType: childStep.stepType,
      stepId: childNode.step.id,
      incomingEdgeCount: childStep.incomingEdges.length,
      incomingEdges: childStep.incomingEdges.map((edge) => ({
        toStepDataName: edge.toStepData.name,
        fromStepId: edge.fromStep.id,
      })),
    });
    return null;
  }

  const parentStep = analysis.steps.find((step) => step.id === datasetInputEdge.fromStep.id);
  if (!parentStep) {
    return null;
  }

  const outputStepData = parentStep.outputStepDatas.find(
    (stepData) => stepData.name === datasetInputEdge.fromStepData.name
  );
  if (!outputStepData) {
    return null;
  }

  // Explicitly type the result to ensure it matches PipelineViewLineageNode structure
  const parentNode: PipelineViewLineageNode = {
    __typename: 'LineageNode',
    step: parentStep,
    stepOutputReference: {
      __typename: 'StepOutputReferenceNode',
      outputStepData,
      rawDataIndex: datasetInputEdge.fromStepDataIndex,
    },
  };

  return parentNode;
}

function onToggleFlaggedRows(
  analysis: PipelineGraphQL,
  view: PipelineViewGraphQL,
  persistedDataFrame: PersistedDataFrameGraphQL,
  clientId: string,
  selectedRowIndices: Set<number>,
  flaggedRowIndices: Set<string>,
  isAddingFlag: boolean
): void {
  const lastNode = view.lineage[view.lineage.length - 1];
  const lastNodeStep = resolveLineageStep(analysis.steps, lastNode);
  const parentNode = isViewTerminalStepType(lastNodeStep.stepType)
    ? getParentNodeFromAnalysisGraph(analysis, lastNode)
    : lastNode;

  assert(parentNode != null, 'Expected to find parent step in analysis graph for terminal step', {
    stepType: lastNodeStep.stepType,
    stepId: lastNode.step.id,
    hasIncomingEdges: lastNodeStep.incomingEdges.length > 0,
  });
  assert(
    parentNode.stepOutputReference != null && parentNode.stepOutputReference.rawDataIndex != null,
    'Expected parent node to have an step output reference and raw data index. Are we rendering flagging button on non succeeded views?'
  );

  const parentNodeStep = resolveLineageStep(analysis.steps, parentNode);
  const parentOutputStepData = resolveOutputStepData(analysis.steps, {
    step: parentNode.step,
    stepOutputReference: parentNode.stepOutputReference,
  });

  recordAnalysisAction(isAddingFlag ? 'FLAG_ROWS' : 'CLEAR_FLAG_ROWS', {
    analysisId: analysis.id,
    lastNodeStepType: lastNodeStep.stepType,
    parentStepType: parentNodeStep.stepType,
    selectedRowCount: selectedRowIndices.size,
  });

  const legacyParentStep = getLegacyStepFromAnalysis(analysis, parentNode.step);

  const rawData = parentOutputStepData.rawDatas[parentNode.stepOutputReference.rawDataIndex];

  const childSteps = getChildStepsForDataFrame(analysis, legacyParentStep, rawData.id);

  const outputDataReferences =
    childSteps.length > 0
      ? childSteps.flatMap((childStep) =>
          childStep.inputStepDatas
            .filter((stepData) => stepData.dataFrames.some((dataFrame) => dataFrame.id === rawData.id))
            .map((inputStepData) => ({
              stepId: childStep.id,
              stepDataName: inputStepData.name,
            }))
        )
      : undefined;

  openFlagRowsModal({
    analysis,
    selectedRowIds: new Set([...selectedRowIndices].map((index) => String(index))),
    flaggedRowIds: flaggedRowIndices,
    isAddingFlag,
    parentStepReference: {
      stepReference: {stepId: parentNode.step.id, type: StepReference.STEP_ID},
      stepDataName: parentOutputStepData.name,
    },
    outputDataReferences,
    onFinalizeFlagging: () => {
      rowSelectionState.deleteState(clientId);
    },
  });
}
